import { FC, useEffect, useMemo, useState } from 'react';
import {
  Button,
  dismissToasts,
  Icon,
  Select,
  SelectOption,
  Spacer,
  TextBody,
  TextSubtitle,
  TextTitle,
  RadioGroup,
  Radio,
} from '@pypestream/design-system';
import {
  TranslationComponent,
  TranslationKeys,
  useTranslation,
} from '@pypestream/translations';

import { ActiveSolutionUsersAvatars } from '../..';
import { GenericModal, GenericModalProps } from '..';

import './publish-modal-warning-banner.scss';
import {
  computeActiveCoworkers,
  computeSeededBots,
  publishCompletionHasNoWatcher,
} from './publish-modal.utils';
import { tableStore, ValidationMode } from '../../../stores/table-store';
import { resolvePublishBlock } from './node-number-publish-block';
import { capitalizeWords } from '../../../utils';
import {
  markPublishFailed,
  markPublishStarted,
  notifyPublishComplete,
  PUBLISH_IN_PROGRESS_TOAST_ID,
} from '../../../utils/watch-publish-status';
import { sendUserMessage } from '../../../xstate/app.xstate-utils';
import {
  sendProStudioEvent,
  useProStudioCtxSelector,
} from '../../../xstate/app.xstate';
import { useBobcatFlag } from '../../../hooks/use-bobcat-flag';
import { publishToastCopy } from './publish-toast-copy';

export type PublishModalProps = GenericModalProps & {
  draftVersionId?: string;
  /** The environment pre-selected when the modal opens (Deploy's switch). */
  initialEnv?: string;
};

export const PublishModal: FC<PublishModalProps> = ({
  draftVersionId,
  initialEnv = 'sandbox',
  modalRef,
  onClose: onCloseModal,
}) => {
  const [t] = useTranslation();
  const toastCopy = publishToastCopy(
    useBobcatFlag('fiveStageNav') === 'allowed'
  );
  const {
    envs,
    botType,
    bots,
    solutionUserPresences,
    userInfo,
    projectUsers,
    draftSolutionVersionId,
    surveyLinkedSolutions,
    isLoadingPublishModalData,
    publishModalSolutionEnvs,
  } = useProStudioCtxSelector((ctx) => ({
    envs: ctx.envs || [],
    botType: ctx.selectedBot?.botType,
    bots: ctx.solutions || [],
    solutionUserPresences: ctx.solutionUserPresences,
    userInfo: ctx.userInfo,
    projectUsers: ctx.projectUsers,
    draftSolutionVersionId: ctx.draftVersionSolutionData?.id ?? null,
    surveyLinkedSolutions: ctx.surveyLinkedSolutions,
    isLoadingPublishModalData: ctx.isLoadingPublishModalData ?? false,
    publishModalSolutionEnvs: ctx.publishModalSolutionEnvs,
  }));

  // Not gated on botType — surveys must warn about coworkers too (PSTUDIO-3597).
  const activeCoworkers = useMemo(
    () => computeActiveCoworkers(solutionUserPresences, userInfo?.id),
    [solutionUserPresences, userInfo?.id]
  );

  const hasActiveCoworkers = activeCoworkers.length > 0;
  const isSurvey = botType === 'survey';
  // While the survey's linked-solution data is loading, block the controls that
  // depend on it: the main-solution <Select> and the Publish button.
  const isLoadingData = isSurvey && isLoadingPublishModalData;

  const [selectedEnv, setSelectedEnv] = useState<string>(initialEnv);
  useEffect(() => setSelectedEnv(initialEnv), [initialEnv]);
  const [selectedBots, setSelectedBots] = useState<string[]>([]);
  const [userEditedBots, setUserEditedBots] = useState(false);

  // Pre-select the main solutions this survey is already linked to (per env),
  // unless the user manually changed the selection while the modal is open.
  // Note: once the user edits the selection, switching the environment no longer
  // re-seeds (the manual choice wins) until the modal is closed and reopened.
  // The decision is computed by `computeSeededBots` (unit-tested in isolation);
  // `null` means "leave the current selection untouched".
  useEffect(() => {
    const seeded = computeSeededBots({
      isSurvey,
      userEditedBots,
      selectedEnv,
      surveyLinkedSolutions,
    });
    if (seeded !== null) {
      setSelectedBots(seeded);
    }
  }, [isSurvey, surveyLinkedSolutions, selectedEnv, userEditedBots]);

  const onClose = () => {
    onCloseModal?.();
    setSelectedEnv(initialEnv);
    // selectedBots is intentionally not cleared here — the seeding effect re-seeds it when userEditedBots resets.
    setUserEditedBots(false);
  };

  const onPublish = async () => {
    if (!selectedEnv || !draftVersionId || !botType) {
      return;
    }

    // Reset any prior failed/handled state for this version so a fresh attempt
    // can report success again (and isn't suppressed by a stale failure flag).
    if (draftSolutionVersionId) {
      markPublishStarted(draftSolutionVersionId);
    }

    tableStore.setSolutionVersionPublishingInProgress(true);
    onClose();
    tableStore.setState('viewing');
    sendUserMessage({
      type: 'info',
      header:
        t(toastCopy.header.key, {
          defaultValue: toastCopy.header.defaultValue,
        }) ?? undefined,
      text:
        t(toastCopy.text.key, {
          defaultValue: toastCopy.text.defaultValue,
        }) ?? '',
      toastId: PUBLISH_IN_PROGRESS_TOAST_ID,
      autoClose: false,
    });

    // Ensure all pending node saves are committed to DB before the backend
    // reads them for compilation — prevents newly inserted nodes from being
    // missing from the published version and the subsequent draft.
    try {
      await tableStore.waitForPendingSaves();
    } catch {
      tableStore.setSolutionVersionPublishingInProgress(false);
      dismissToasts(PUBLISH_IN_PROGRESS_TOAST_ID);
      sendUserMessage({
        type: 'error',
        text:
          t(
            'pro-studio/navigation:publish_modal.toast.save_failed_before_publish',
            {
              defaultValue:
                'Failed to save pending changes. Please try publishing again.',
            }
          ) ?? '',
        autoClose: 5000,
      });
      return;
    }

    // PSTUDIO-4000 + PSTUDIO-3990: pre-publish gate (second line of defence behind
    // the reactive canPublish disable). A duplicate node number — or a missing
    // one — makes the published solution unusable, yet either can reach `rows`
    // without ever refreshing errors (an orders-only save, a RAW paste, a series
    // of undo/redo that left validation stale). This guard is NOT redundant with
    // canPublish: `await waitForPendingSaves()` above opens a window in which a
    // coworker merge, a cross-tab reload or a deferred panel commit can rewrite
    // `rows` after the button was enabled.
    //
    // Detect SYNCHRONOUSLY from the CURRENT rows (same source, and now the same
    // single-pass predicate, as canPublish), so the happy path pays no extra
    // round-trip. `rowsForValidation`, NOT `rows`: it is what canPublish reads,
    // so the two echelons cannot disagree, and it drops the rows a delete has
    // already claimed but whose idle callback has not compacted yet — otherwise
    // this guard would refuse to publish over a row the user has removed. Only when a problem IS present force a recompile
    // (validateAndSync), which lights up the offending cells and populates the
    // popover, then refuse to publish. The block itself does not depend on that
    // recompile, so a failed/short-circuited one can never let an invalid
    // solution through. Which message is shown — and why saying "duplicate" for
    // a blank cell was itself the ticket's defect — lives in the resolver.
    // PSTUDIO-4120: the same window also covers a validation pass that finished
    // WITHOUT a verdict (it threw, or the compiler answered with its own crash).
    // `canPublish` refuses on that too, so this echelon has to as well, or the
    // two disagree in exactly the window this guard exists for. The forced
    // The forced `validateAndSync` below is the way OUT for two of the three
    // reasons and not the third, which is worth stating rather than implying:
    // `threw` and `compiler-degraded` reset the compiler when they are recorded,
    // so the forced pass runs against a clean one; `payload-not-compilable`
    // deliberately does NOT reset (the pass never reached the compiler), and the
    // forced pass will skip again for as long as the unreadable node is there —
    // the way out of that one is fixing the cell the block names.
    const nodeNumberBlock = resolvePublishBlock(
      tableStore.rowsForValidation,
      tableStore.validationFailure
    );
    if (nodeNumberBlock) {
      await tableStore.validateAndSync(ValidationMode.Auto);
      tableStore.setSolutionVersionPublishingInProgress(false);
      dismissToasts(PUBLISH_IN_PROGRESS_TOAST_ID);
      tableStore.setState('editing');
      sendUserMessage({
        type: 'error',
        text:
          t(`pro-studio/navigation:${nodeNumberBlock.key}`, {
            defaultValue: nodeNumberBlock.defaultValue,
          }) ?? '',
        autoClose: 5000,
      });
      return;
    }

    sendProStudioEvent({
      type: 'publishBot',
      payload: {
        env: selectedEnv,
        version: draftVersionId,
        botType,
        bots: selectedBots,
        onPublishComplete: (result) => {
          if (result.status === 'error') {
            // The publish failed — record it so the status watchers can't show a
            // false success when the row reverts to ACTIVE, then surface the error.
            if (result.solutionVersionId) {
              markPublishFailed(result.solutionVersionId);
            }
            tableStore.setSolutionVersionPublishingInProgress(false);
            dismissToasts(PUBLISH_IN_PROGRESS_TOAST_ID);
            tableStore.setState('editing');
            sendUserMessage({
              type: 'error',
              text:
                t('pro-studio/navigation:publish_modal.toast.publish_failed', {
                  defaultValue:
                    'There was an error publishing this solution. Please reach out to your Pypestream point of contact for assistance',
                }) ?? '',
              autoClose: 5000,
            });
            return;
          }

          // PSTUDIO-3985: completion is announced by
          // `usePublishStatusWatcherForCoworkers`, mounted once at the router
          // (`router.tsx`) and subscribed to the version currently LOADED. This
          // modal used to open a second watcher on the published version and
          // discard its cleanup, so the subscription outlived the visit — and
          // its effect (`restorePublishUiState`) is global: when version A
          // finally reached ACTIVE it forced whatever solution B the user had
          // moved on to back into edit mode, cleared B's own publish lock and
          // announced A's success over B. The router watcher is scoped to the
          // loaded version by construction, and it already performs this exact
          // announcement on PUBLISHING -> ACTIVE, so the second one was pure
          // cross-solution leakage.
          // ...except when nothing is subscribed at all: that watcher keys off
          // the loaded draft version id, and a solution without one publishes
          // through `createAndPublishSolutionVersion`, whose brand-new id nobody
          // is listening to. Then this modal is the only thing that can undo
          // what `onPublish` raised — all three parts of it, mode included, as
          // the error branch above does.
          if (publishCompletionHasNoWatcher(draftSolutionVersionId)) {
            if (result.solutionVersionId) {
              // `notifyPublishComplete` is the same announcement the router
              // watcher makes: it restores all three pieces of UI state through
              // `restorePublishUiState` AND confirms the publish. Restoring
              // without it left this path silently successful — the banner went
              // away and nothing said the solution had shipped.
              notifyPublishComplete(
                result.solutionVersionId,
                t(toastCopy.success.key, {
                  defaultValue: toastCopy.success.defaultValue,
                }) ?? ''
              );
            } else {
              // No version id to announce or de-duplicate against, so restore
              // by hand rather than strand the user in view mode.
              tableStore.setSolutionVersionPublishingInProgress(false);
              dismissToasts(PUBLISH_IN_PROGRESS_TOAST_ID);
              tableStore.setState(tableStore.isViewer ? 'viewing' : 'editing');
            }
          }
        },
      },
    });
  };

  return (
    <GenericModal
      test-id="ps-publish-version-modal"
      modalRef={modalRef}
      onClose={onClose}
    >
      <TextTitle size="small">
        <TranslationComponent i18nKey="pro-studio/navigation:publish_modal.title.part1">
          Publish
        </TranslationComponent>{' '}
        {isSurvey && (
          <>
            <TranslationComponent i18nKey="pro-studio/navigation:publish_modal.title.solution_type">
              Survey
            </TranslationComponent>{' '}
          </>
        )}
        <span color="primary">{draftVersionId}</span>
      </TextTitle>
      <Spacer />
      {hasActiveCoworkers && (
        <>
          <div className="c-publish-modal-warning-banner" role="alert">
            <span className="c-publish-modal-warning-banner__icon">
              <Icon name="alert" color="gold" size="medium" />
            </span>
            <div className="c-publish-modal-warning-banner__text">
              <TextBody size="small">
                <TranslationComponent i18nKey="pro-studio/navigation:publish_modal.active_coworkers.banner_message">
                  Other people are working on this solution. Review any changes
                  before publishing. Publishing now may discard their
                  in-progress edits.
                </TranslationComponent>
              </TextBody>
            </div>
            <div className="c-publish-modal-warning-banner__avatars">
              <ActiveSolutionUsersAvatars
                presences={activeCoworkers}
                projectUsers={projectUsers}
                variant="inline-compact"
                maxVisible={3}
              />
            </div>
          </div>
          <Spacer size="small" />
        </>
      )}
      <TextSubtitle
        test-id="ps-publish-version-modal-select-env-title"
        size="small"
      >
        <TranslationComponent i18nKey="pro-studio/navigation:publish_modal.form.part1.title">
          Select an environment:
        </TranslationComponent>
      </TextSubtitle>
      <Spacer size="small" />
      <RadioGroup
        value={selectedEnv}
        orientation="horizontal"
        size="small"
        test-id="ps-publish-version-modal-select-env"
        onChange={(e) => setSelectedEnv(e.target.value as string)}
      >
        {[...envs].reverse().map((env) => (
          <Radio key={env} value={env}>
            {capitalizeWords(env)}
          </Radio>
        ))}
      </RadioGroup>
      {isSurvey && (
        <>
          <Spacer size="large" />
          <TextSubtitle size="small">
            <TranslationComponent i18nKey="pro-studio/navigation:publish_modal.form.part2.title">
              For the following solutions:
            </TranslationComponent>
          </TextSubtitle>
          <Spacer size="small" />
          <Select
            test-id="ps-publish-version-modal-select-main-solution"
            multiple
            disabled={isLoadingData}
            placeholder={
              isLoadingData
                ? t(
                    'pro-studio/navigation:publish_modal.form.part2.select.loading_placeholder',
                    {
                      defaultValue: 'Loading solutions…',
                    }
                  ) || ''
                : t(
                    'pro-studio/navigation:publish_modal.form.part2.select.placeholder',
                    {
                      defaultValue: 'Select solution',
                    }
                  ) || ''
            }
            variant="outlined"
            value={selectedBots}
            onChange={(e) => {
              const value = e.target.value as string[];
              setSelectedBots(value);
              setUserEditedBots(true);
            }}
          >
            {bots
              .filter(
                ({ botType: botTypeFromBot }) => botTypeFromBot !== 'survey'
              )
              ?.map((bot, index) => {
                // Environments where this main is actually linkable (channel
                // bound to it AND a published version), from loadPublishModalData.
                // The option is enabled only when it is linkable in the currently
                // selected env; the label lists every env it is linkable in (e.g.
                // "(Sandbox, Live)"), which also explains why a disabled option is
                // disabled (it shows the env(s) where it IS available, or none).
                const linkableEnvs =
                  publishModalSolutionEnvs?.[bot.id ?? ''] ?? [];
                const isLinkableInSelectedEnv = linkableEnvs.includes(
                  selectedEnv.toLowerCase()
                );
                const envLabel = linkableEnvs.length
                  ? `(${linkableEnvs
                      .map((env) => capitalizeWords(env))
                      .join(', ')})`
                  : '(not yet published)';

                return (
                  <SelectOption
                    test-id={`ps-publish-${bot.displayName?.toLowerCase()}-option`}
                    key={`${bot}-${index}`}
                    value={bot.id}
                    disabled={!isLinkableInSelectedEnv}
                  >
                    {`${bot.displayName} ${envLabel}`}
                  </SelectOption>
                );
              })}
          </Select>
          <Spacer size="large" />
        </>
      )}
      <Button
        test-id="ps-publish-version-modal-publish-btn"
        size="large"
        slot="footer"
        // Keep `disabled` in lock-step with onPublish's guard
        // (`!selectedEnv || !draftVersionId || !botType`). `botType` comes from
        // `ctx.selectedBot`, which XState resets to undefined for a beat on
        // navigation and after every publish; without these the button is
        // clickable while onPublish silently no-ops (the publish mutation never
        // fires), which both confuses users and makes the e2e click a no-op.
        disabled={!selectedEnv || !draftVersionId || !botType || isLoadingData}
        onClick={onPublish}
      >
        <TranslationComponent
          i18nKey={
            `pro-studio/navigation:publish_modal.button.${
              hasActiveCoworkers ? 'publish_anyway' : 'static'
            }` as TranslationKeys
          }
        >
          {hasActiveCoworkers ? 'Publish anyway' : 'Publish'}
        </TranslationComponent>
      </Button>
    </GenericModal>
  );
};
